import { Button } from 'antd';
import { UserData } from 'hydra-data-types';
import React, { FunctionComponent, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { omit } from 'ts-utils';
import { Color } from '../../config/Color';
import { ActionType } from '../../store/ActionTypes';
import { createMockUser, mockUserData } from '../../store/MockData';
import { store, useAppStore } from '../../store/Store';
import {
  AssessmentUser,
  CompletedUser,
  IncompleteUser,
  UserState,
} from '../../store/user/UserState';
import { Heading } from '../styles/Typography';
import { Screens, screenToSideLinkTable } from './DemoScreenConfigs';

// Demonstration of all current app screens
export const DemoScreens: FunctionComponent = () => {
  const [screenSize, setScreenSize] = useState('medium');
  const [hasLoadedState, setHasLoadedState] = useState(false);
  const hasFullUser = useAppStore((state) => state.user.type === 'email');

  const demoIncompleteUser: IncompleteUser = {
    name: 'Harry',
    type: 'partial',
    userId: 'tiGu5caSwxTcrDm41BBof6DgHmN2',
    email: 'harry@potter.co',
    tempPassword: 'igZEhJEx',
    hasPaid: true,
    setUserPassword: true,
  };

  useEffect(function initializeUserData() {
    setUserData();
  }, []);

  useEffect(
    function setLoadedState() {
      if (hasFullUser) {
        setHasLoadedState(true);
      }
    },
    [hasFullUser]
  );

  async function setUserData() {
    const current = store.getState();
    const userData = { ...mockUserData, ...current.userData } as UserData;
    const mockUser =
      current.user === undefined || current.user.type === 'partial'
        ? ((await createMockUser({ user: current.user, userData })) ?? current.user)
        : current.user;
    const mockAuthState = (mockUser: AssessmentUser): UserState => ({
      ...mockUser,
      loading: false,
      error: undefined,
      hasPaid: false,
      setUserPassword: true,
    });
    const user = mockAuthState(mockUser);

    store.dispatch({
      type: ActionType.LOGIN_USER_SUCCESS,
      user: omit(user, ['loading', 'error']) as CompletedUser,
      userData,
      method: 'emailAndPassword',
    });

    store.dispatch({ type: ActionType.MERGE_USER_DATA, userData });

    // This dispatch introduces firebase permissions errors in the console as it changes a user back to
    // a partial user after rendering auth pages as a full user but is the only way to display both
    // auth pages and partial pages
    store.dispatch({
      type: ActionType.SETUP_INCOMPLETE_USER_SUCCESS,
      user: demoIncompleteUser,
      paid: false,
      setUserPassword: false,
    });
  }

  const ScreenLinks = (): JSX.Element => {
    return (
      <LinksContainer>
        <p>Screens</p>
        <ul>
          {Object.values(screenToSideLinkTable)
            .sort((linkA, linkB) => linkA.title.localeCompare(linkB.title))
            .map(({ title, screenName }) => (
              <li key={title}>
                <a onClick={() => scrollToElement(screenName)}>{title}</a>
              </li>
            ))}
        </ul>
        <p>Screen size</p>
        <ButtonContainer>
          <Button onClick={() => setScreenSize('small')}>Small</Button>
          <Button onClick={() => setScreenSize('medium')}>Medium</Button>
          <Button onClick={() => setScreenSize('large')}>Large</Button>
        </ButtonContainer>
      </LinksContainer>
    );
  };

  function scrollToElement(id: string) {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  return (
    <DemoScreensContainer>
      <ScreenLinks />
      {hasLoadedState ? <Screens screenSize={screenSize} /> : <div>Loading...</div>}
    </DemoScreensContainer>
  );
};

const LinksContainer = styled.div`
  top: 150px;
  z-index: 9999;
  background-color: white;
  position: fixed;
  left: 1em;
  border: 1px solid black;
  text-align: left;
  ul {
    padding: 1em;
    list-style-type: none;
    margin: 0;
  }
  p {
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    padding: 1em 1em 0;
    margin-bottom: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  button {
    margin: ${({ theme }) => `${theme.spacing._6px} 0`};
  }
  button:last-child {
    margin-bottom: 0;
  }
`;

const DemoScreensContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-top;
`;

export const DemoScreensWrapper = styled.div<{
  isFullSize: boolean;
  isSmallSize: boolean;
  isMediumSize: boolean;
  isLargeSize: boolean;
}>`
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => theme.spacing._20px};
  ${({ isFullSize }) =>
    isFullSize &&
    css`
      zoom: 1 !important;
      > div {
        height: auto;
      }
    `}
  ${({ isSmallSize }) =>
    isSmallSize &&
    css`
      zoom: 0.5;
    `}
	${({ isMediumSize }) =>
    isMediumSize &&
    css`
      zoom: 0.7;
    `}
	${({ isLargeSize }) =>
    isLargeSize &&
    css`
      zoom: 1;
    `}
`;

export const DemoScreensHeader = styled(Heading)`
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing._6px} 0`};
  margin: 0;
  background: ${Color.decorative.default};
  span {
    float: right;
    padding-right: ${({ theme }) => theme.spacing._10px};
  }
`;
export const DemoScreensComponent = styled.div`
  position: relative;
  border: 1.5px solid ${Color.decorative.default};
  width: 400px;
  padding: ${({ theme }) => `0 ${theme.spacing._26px} ${theme.spacing._26px}`};
  overflow: scroll;
  min-height: 60vh;
  height: 60vh;
`;
